import { ProviderAdjustmentReportItem } from '../../api';
import { AdjustmentCodes } from '../dictionary';
import { adjudicationGroupKey, formatCodeComposite, IDenialCode, parseDenialCode } from './denial-code';
import { isRoutineAdjustment } from './routine-adjustments';

/**
 * Turns the adjustment report into the rows the denials table shows.
 *
 * ## What this can and cannot say, and why the row shape is so thin
 *
 * `getAdjustmentReport` returns `{group, reasonCode, count, amount}` and takes only a date range. What the two
 * numbers are was measured against the remits themselves on production (2026-09-16), because the field descriptions
 * are wrong or missing:
 *
 * - **`count` is REMITS — claim payments carrying the code — not adjustments and not claims.** A payment carrying
 *   the code on three service lines counts once, whatever the description says ("The number of matching
 *   adjustments"). A claim can have several payments (a reversal and a reprocess), so it is not a claim count either.
 * - **`amount` is not used.** It sums, over those payments, each payment's AVERAGE adjustment for the code, so it
 *   understates the adjusted dollars wherever a payment carries the code on more than one line — by up to half on
 *   the groups checked. It is neither total charge nor total paid, and no honest column can be built from it.
 * - **No total across rows.** A payment carrying two of the codes counts under both.
 * - **No remark code.** RARCs come from a separate report (`getRemarkReport`) that is not joined to
 *   this one, so `CO:197.N54` cannot be built — only `CO:197`.
 * - **No payer or provider scope.** Every row is "All payers" because nothing else is expressible.
 *
 * Those gaps are asks on FE-73. Nothing here fills them by inference.
 */
export interface IDenialGroupRow {
    /** The grouping key both this table and a worker configuration must agree on. */
    key: string;
    /** The parsed code the row groups on — what a worker rule for this group is built from (FE-75). */
    code: IDenialCode;
    /** Always "All payers" today — the endpoint takes no payer or provider parameter. */
    scope: string;
    /** `CO:197`. No remark half until the two reports are joined. */
    codes: string;
    /** The official CARC text, or a marked fallback when the code is not in the dictionary. */
    reasonDescription: string;
    /** Remits carrying the code — NOT adjustments, NOT claims, and not additive across rows. See the note above. */
    remits: number;
    /**
     * A routine adjustment on a paid claim (CO-45, PR-1/2/3) rather than a denial. Hidden by default; see
     * `routine-adjustments.ts` for why the list is exactly this short.
     */
    routine: boolean;
}

export interface IDenialGroupRows {
    rows: IDenialGroupRow[];
    /**
     * Report items that could not be read as a denial code.
     *
     * Returned rather than silently dropped: a table that quietly discards rows shows a total that
     * does not match the practice's remits and gives no hint why (NS-3).
     */
    skipped: number;
}

/** Shown when the payer sent a code the published CARC list does not contain. */
export const UNKNOWN_REASON = 'No description for this code in the CARC list';

export const buildDenialGroupRows = (items: ProviderAdjustmentReportItem[] | undefined): IDenialGroupRows => {
    let skipped = 0;
    const rows: IDenialGroupRow[] = [];

    (items || []).forEach(item => {
        const code = parseDenialCode(item.group, item.reasonCode);
        if (!code) {
            skipped += 1;
            return;
        }
        rows.push({
            key: adjudicationGroupKey(code),
            code,
            scope: 'All payers',
            codes: formatCodeComposite(code),
            reasonDescription:
                (AdjustmentCodes as Record<string, string>)[code.reasonCode] || UNKNOWN_REASON,
            remits: item.count || 0,
            routine: isRoutineAdjustment(code),
        });
    });

    // Biggest first. The design sorts by money, which the report cannot give yet; volume is the honest
    // stand-in and puts the code worth automating at the top, which is the point of the page.
    rows.sort((a, b) => b.remits - a.remits || a.codes.localeCompare(b.codes));

    return { rows, skipped };
};

/** The rows the table shows: routine adjustments only when asked for. */
export const visibleDenialRows = (rows: IDenialGroupRow[], showRoutine: boolean): IDenialGroupRow[] =>
    showRoutine ? rows : rows.filter(row => !row.routine);

/** A code with its separators and spaces removed: `co:197.n54` → `co197n54`. */
const compactCode = (code: string): string => code.replace(/[\s:.-]/g, '');

/**
 * The rows matching a search by code or by official description, ignoring case. A blank query matches every row.
 *
 * Codes match with or without their separator — `CO:16`, `CO-16` and `co16` all find `CO:16` — because people
 * paste codes from remits and payer letters in whichever form those used. The group stays part of the match, so
 * `CO-16` does not also find `PR:16`: the same reason code under another group is a different adjustment.
 */
export const searchDenialRows = (rows: IDenialGroupRow[], query: string): IDenialGroupRow[] => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
        return rows;
    }
    const compactNeedle = compactCode(needle);
    return rows.filter(
        row =>
            row.reasonDescription.toLowerCase().includes(needle) ||
            (compactNeedle.length > 0 && compactCode(row.codes.toLowerCase()).includes(compactNeedle)),
    );
};
